import {
  Button,
  Callout,
  EmptyState,
  Icon,
  Skeleton,
} from "@obsina/design-system";
import { Network, RefreshCw } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ControlPlaneError } from "./api";
import type { AuthenticatedAccount } from "./App";
import { AppShell } from "./AppShell";
import { enMessages } from "./messages";
import type { AppMessages } from "./messages";
import type { AppView } from "./navigation";
import {
  buildInspectorModel,
  buildNarrowClusterCards,
  loadFullMemoryGraph,
} from "./memory-graph/data";
import { computeGraphLayout } from "./memory-graph/layout";
import {
  coveragePartialNotice,
  foldForSearch,
  GraphView,
  reasonSentence,
} from "./memory-graph/GraphView";
import { InspectorPanel } from "./memory-graph/InspectorPanel";
import type { InspectorNeighbour } from "./memory-graph/InspectorPanel";
import { NarrowClusterList } from "./memory-graph/NarrowClusterList";
import { GRAPH_ZOOM, LAYOUT_REFERENCE } from "./memory-graph/types";
import type {
  GraphInteractionState,
  InspectorModel,
  LayoutResult,
  MemoryGraphData,
  MemoryGraphMessages,
  NarrowClusterCard,
  ResolvedMemoryGraph,
  UnknownMemoryGraph,
} from "./memory-graph/types";

type LoadErrorKind = "unauthorized" | "unavailable" | "generic";

/**
 * Fixed deterministic layout seed. Equal graph input always lays out the same
 * way regardless of wall clock (types.ts §4). The viewport is a fixed reference:
 * `computeGraphLayout` normalizes all geometry to the unit box, so only the
 * reference aspect matters and no live measurement is needed to stay stable.
 */
const MEMORY_GRAPH_SEED = 20260717;
const GRAPH_VIEWPORT = {
  width: LAYOUT_REFERENCE.width,
  height: LAYOUT_REFERENCE.height,
} as const;

/** Below this width the full canvas is replaced by the honest cluster list. */
const NARROW_QUERY = "(max-width: 720px)";

const EMPTY_LAYOUT: LayoutResult = {
  nodes: [],
  edges: [],
  minimap: [],
  seed: MEMORY_GRAPH_SEED,
};

function classifyError(error: unknown): LoadErrorKind {
  if (error instanceof ControlPlaneError) {
    if (error.status === 401) return "unauthorized";
    if (error.status === 503) return "unavailable";
  }
  return "generic";
}

interface GraphState {
  data: MemoryGraphData | null;
  queryAsOf: string | null;
  initialLoading: boolean;
  refreshing: boolean;
  errorKind: LoadErrorKind | null;
  reload: () => void;
}

/**
 * Loads the one view this page has: the whole authorized network, enumerated.
 *
 * The commitment-seeded scope that used to sit beside it is not offered while
 * claim extraction is off — it is seeded from open commitments, so with no
 * commitments it would draw nothing, and a toggle whose other position is always
 * empty is a control that does not work. `loadMemoryGraph` is kept intact for
 * when extraction returns.
 */
function useMemoryGraph(apiBase: string | null): GraphState {
  const [data, setData] = useState<MemoryGraphData | null>(null);
  const [queryAsOf, setQueryAsOf] = useState<string | null>(null);
  const [loading, setLoading] = useState(Boolean(apiBase));
  const [errorKind, setErrorKind] = useState<LoadErrorKind | null>(null);
  const [revision, setRevision] = useState(0);
  const reload = useCallback(() => setRevision((value) => value + 1), []);

  useEffect(() => {
    if (!apiBase) {
      setLoading(false);
      setErrorKind("generic");
      return;
    }
    const controller = new AbortController();
    const nowIso = new Date().toISOString();
    setLoading(true);
    setErrorKind(null);
    loadFullMemoryGraph(apiBase, nowIso, controller.signal)
      .then((result) => {
        if (controller.signal.aborted) return;
        setData(result);
        setQueryAsOf(nowIso);
      })
      .catch((error: unknown) => {
        if (!controller.signal.aborted) setErrorKind(classifyError(error));
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [apiBase, revision]);

  return {
    data,
    queryAsOf,
    initialLoading: loading && !data,
    refreshing: loading && Boolean(data),
    errorKind,
    reload,
  };
}

interface InspectorState {
  model: InspectorModel | null;
  loading: boolean;
  failed: boolean;
  retry: () => void;
}

/**
 * Loads the focused entity's inspector model. A change of `entityId` (or unmount)
 * aborts the in-flight request, so rapid focus changes never race. A null entity
 * clears the model rather than fabricating a stale one.
 *
 * An entity already read under this same answer is served from memory. Reading a
 * map means clicking from one node to a neighbour and back, and every return trip
 * used to re-pay the whole waterfall — measured on the e2e harness at 300 ms per
 * request, re-opening an entity cost as much as opening it the first time. The
 * cache is keyed by the coverage cut-off the model was built under and thrown
 * away whole when that cut-off moves (a refresh, a re-query), so it can never
 * show an answer from one `asOf` beside a map drawn at another.
 */
function useInspector(
  apiBase: string | null,
  entityId: string | null,
  asOf: string | null,
): InspectorState {
  const [model, setModel] = useState<InspectorModel | null>(null);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const cache = useRef(new Map<string, InspectorModel>());
  const cacheKey = useRef<string | null>(null);
  const retry = useCallback(() => setAttempt((value) => value + 1), []);

  useEffect(() => {
    if (!apiBase || !entityId || !asOf) {
      setModel(null);
      setFailed(false);
      setLoading(false);
      return;
    }
    const key = `${apiBase}${asOf}`;
    if (cacheKey.current !== key) {
      cacheKey.current = key;
      cache.current = new Map();
    }
    // A retry only follows a failure, so nothing is cached for it; dropping the
    // entry anyway keeps "try again" meaning a fresh read in every state.
    if (attempt > 0) cache.current.delete(entityId);
    const cached = cache.current.get(entityId);
    if (cached) {
      setModel(cached);
      setFailed(false);
      setLoading(false);
      return;
    }
    const controller = new AbortController();
    setLoading(true);
    setFailed(false);
    setModel(null);
    buildInspectorModel(
      apiBase,
      entityId,
      asOf,
      controller.signal,
      // The panel without its excerpts, a round trip early. Never cached: only
      // the finished read is worth serving again.
      (partial) => {
        if (!controller.signal.aborted) setModel(partial);
      },
    )
      .then((result) => {
        if (controller.signal.aborted) return;
        cache.current.set(entityId, result);
        setModel(result);
      })
      .catch(() => {
        if (controller.signal.aborted) return;
        // A read that failed after the early hand-over must not leave its half
        // on screen: the section would sit "still reading" for good.
        setModel(null);
        setFailed(true);
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [apiBase, entityId, asOf, attempt]);

  return { model, loading, failed, retry };
}

/**
 * Tracks the narrow breakpoint through `matchMedia`. Defaults to the wide layout
 * and only narrows on a real match, so an environment without `matchMedia`
 * (jsdom) keeps the full canvas rather than collapsing to the fallback.
 */
function useIsNarrow(): boolean {
  const [isNarrow, setIsNarrow] = useState(false);
  useEffect(() => {
    if (
      typeof window === "undefined" ||
      typeof window.matchMedia !== "function"
    ) {
      return;
    }
    const query = window.matchMedia(NARROW_QUERY);
    const update = () => setIsNarrow(query.matches);
    update();
    query.addEventListener("change", update);
    return () => query.removeEventListener("change", update);
  }, []);
  return isNarrow;
}

function defaultReloadPage(): void {
  if (typeof window !== "undefined") window.location.reload();
}

/**
 * The full-network size line. The counts are the same either way — every node
 * drawn is authorized and real — but a `partial` answer may not have its size
 * announced as the *full* network while a derived layer over it is unread.
 */
function scaleLine(
  data: ResolvedMemoryGraph,
  messages: MemoryGraphMessages,
): string {
  const partial = data.coverage.state === "partial";
  const line = data.truncated
    ? partial
      ? messages.scale.partialTruncated
      : messages.scale.truncated
    : partial
      ? messages.scale.partial
      : messages.scale.complete;
  return line(data.nodes.length, data.edges.length);
}

export interface MemoryGraphViewProps {
  account?: AuthenticatedAccount | null;
  apiBase: string | null;
  messages?: AppMessages;
  activeView?: AppView;
  onNavigate?: (view: AppView) => void;
  onSignOut?: () => Promise<void> | void;
  /** Injected only in tests; the default performs a full reauthentication reload. */
  reloadPage?: () => void;
}

export function MemoryGraphView({
  account = null,
  apiBase,
  messages = enMessages,
  activeView = "memory_graph",
  onNavigate,
  onSignOut,
  reloadPage = defaultReloadPage,
}: MemoryGraphViewProps) {
  const graph = useMemoryGraph(apiBase);
  const [signingOut, setSigningOut] = useState(false);
  const [focusedNodeId, setFocusedNodeId] = useState<string | null>(null);
  const [hoveredNodeId, setHoveredNodeId] = useState<string | null>(null);
  const [zoom, setZoom] = useState<number>(GRAPH_ZOOM.overviewDefault);
  const [searchValue, setSearchValue] = useState("");
  /** Matches for the live query; null while the box is empty. */
  const [searchMatchCount, setSearchMatchCount] = useState<number | null>(null);
  const [expandedClusterId, setExpandedClusterId] = useState<string | null>(
    null,
  );
  const isNarrow = useIsNarrow();
  const inspector = useInspector(apiBase, focusedNodeId, graph.queryAsOf);

  const data = graph.data;
  const mg = messages.memoryGraph;
  // Only a resolved answer has a size, so only a resolved answer is counted
  // here. An unknown one states — without a number — why the size is not known,
  // keyed by how durable the coverage gap is: a terminal gap must not read as
  // "wait and it will fill". Truncated shows an honest "N+"; there is no silent
  // trim.
  const resolved: ResolvedMemoryGraph | null =
    data?.status === "ok" ? data : null;
  const sizeStatus: string | null =
    data === null
      ? null
      : data.status === "unknown"
        ? mg.scale.unknown[data.coverage.durability ?? "unstated"]
        : scaleLine(data, mg);
  // The narrow layout draws no stage, so it has no bottom strip to carry the
  // shortfall of a partial answer; it states the same sentence above the list.
  const partialNotice = resolved
    ? coveragePartialNotice(resolved.coverage, mg)
    : null;
  const focusedGraphNode =
    focusedNodeId === null
      ? null
      : (resolved?.nodes.find((node) => node.id === focusedNodeId) ?? null);

  // The focused node's neighbours, by name. Read off the same resolved graph the
  // canvas draws, so the list is exactly that node's degree — the count the
  // panel used to print instead of these names. Nothing here reaches past the
  // drawn graph, so it can neither invent an entity nor widen what is
  // authorized; a node the graph dropped simply has no name to show.
  const neighbours = useMemo<readonly InspectorNeighbour[]>(() => {
    if (!resolved || focusedNodeId === null) return [];
    const nodes = new Map(
      resolved.nodes.map((node) => [node.id, node] as const),
    );
    const found: InspectorNeighbour[] = [];
    for (const edge of resolved.edges) {
      const otherId =
        edge.sourceNodeId === focusedNodeId
          ? edge.targetNodeId
          : edge.targetNodeId === focusedNodeId
            ? edge.sourceNodeId
            : null;
      if (otherId === null) continue;
      const other = nodes.get(otherId);
      if (other === undefined) continue;
      found.push({
        id: otherId,
        label: other.label,
        // Carried, never derived. Two neighbours can legitimately hold the same
        // name, and the address is the only thing on this record that says they
        // are two records — see InspectorNeighbour.identifier.
        ...(other.identifier ? { identifier: other.identifier } : {}),
        ...(other.identifierCount === undefined
          ? {}
          : { identifierCount: other.identifierCount }),
        relationType: edge.relationType,
        weight: edge.weight,
        ...(edge.states ? { states: edge.states } : {}),
      });
    }
    return found;
  }, [resolved, focusedNodeId]);

  const layout = useMemo<LayoutResult>(
    () =>
      resolved
        ? computeGraphLayout({
            nodes: resolved.nodes,
            edges: resolved.edges,
            clusters: resolved.clusters,
            seed: MEMORY_GRAPH_SEED,
            viewport: GRAPH_VIEWPORT,
          })
        : EMPTY_LAYOUT,
    [resolved],
  );

  const narrowCards = useMemo<readonly NarrowClusterCard[]>(
    () => (resolved ? buildNarrowClusterCards(resolved) : []),
    [resolved],
  );

  const signOut = () => {
    if (!onSignOut || signingOut) return;
    setSigningOut(true);
    void Promise.resolve(onSignOut()).finally(() => setSigningOut(false));
  };

  const focusNode = useCallback((id: string | null) => {
    setFocusedNodeId(id);
    setZoom(id ? GRAPH_ZOOM.focusDefault : GRAPH_ZOOM.overviewDefault);
  }, []);

  // A refresh can drop the focused entity from the bounded graph — or turn the
  // whole answer unknown; close the inspector then, so a stale detail panel
  // never renders counts that the resolved graph can no longer vouch for.
  useEffect(() => {
    if (!focusedNodeId || !data) return;
    const stillResolved =
      resolved?.nodes.some((node) => node.id === focusedNodeId) ?? false;
    if (!stillResolved) focusNode(null);
  }, [data, resolved, focusedNodeId, focusNode]);

  // Client-side label filter: focus the first entity whose label contains the
  // query (no new endpoint). Clearing the search returns to the overview.
  //
  // The comparison folds diacritics and case (see `foldForSearch`): the plain
  // lowercase `includes` this replaces could not match a single Turkish-spelled
  // name — "İlker" and "Öztürk" never contained "ilker" or "ozturk" — which on
  // this tenant meant typing a name did nothing at all. A query that matches
  // nothing now releases the focus and says so, instead of silently leaving the
  // previous entity focused as if it were the answer.
  const onSearchChange = useCallback(
    (value: string) => {
      setSearchValue(value);
      const query = foldForSearch(value.trim());
      if (query === "") {
        setSearchMatchCount(null);
        focusNode(null);
        return;
      }
      const matches = (resolved?.nodes ?? []).filter((node) =>
        foldForSearch(node.label).includes(query),
      );
      setSearchMatchCount(matches.length);
      focusNode(matches[0]?.id ?? null);
    },
    [resolved, focusNode],
  );

  const toggleCluster = useCallback(
    (clusterId: string) =>
      setExpandedClusterId((current) =>
        current === clusterId ? null : clusterId,
      ),
    [],
  );

  const interaction: GraphInteractionState = {
    focusedNodeId,
    hoveredNodeId,
    keyboardFocusedNodeId: focusedNodeId,
    zoom,
  };

  // The inspector is a non-modal floating card shared by both layouts: over the
  // graph's right side on wide screens (the graph stays interactive, so clicking
  // another node switches its content) and a full-screen sheet on the narrow
  // layout. It renders in whichever layout is active. Fail-closed: content shows
  // only for a resolved graph node and the connection count is that node's degree.
  //
  // Memoized deliberately. Hover lives on this component, so every pointer
  // entering a node used to re-render the panel too — an entity's whole detail
  // card, its named links and its excerpts, rebuilt because the pointer crossed
  // a circle somewhere else on the map. Nothing here depends on what is hovered,
  // so nothing here should be re-rendered by it.
  const inspectorPanel = useMemo(
    () => (
      <InspectorPanel
        failed={inspector.failed}
        loading={inspector.loading}
        mapBounded={resolved?.truncated ?? false}
        messages={mg}
        model={inspector.model}
        neighbours={neighbours}
        node={focusedGraphNode}
        onClose={() => focusNode(null)}
        onFocusNode={focusNode}
        onRetry={inspector.retry}
        open={focusedGraphNode !== null}
      />
    ),
    [
      focusNode,
      focusedGraphNode,
      inspector.failed,
      inspector.loading,
      inspector.model,
      inspector.retry,
      mg,
      neighbours,
      resolved?.truncated,
    ],
  );

  return (
    <AppShell
      account={account}
      activeView={activeView}
      messages={messages}
      onNavigate={onNavigate}
      onSignOut={onSignOut && signOut}
      signingOut={signingOut}
    >
      <div className="page-stack page-stack--memory-graph">
        {/* One heading, one line. The eyebrow above the title was a second
            heading block saying the same thing, and the two-line body under it
            pushed the map down again — on this page every row of prose is a row
            the map does not get, and the map is the page. */}
        <div className="page-intro">
          <h1>{mg.title}</h1>
          <p>{mg.body}</p>
        </div>
        {graph.initialLoading ? (
          <MemoryGraphLoading label={mg.loading} />
        ) : graph.errorKind === "unauthorized" ? (
          <Callout announce title={mg.unauthorized.title} tone="danger">
            <p>{mg.unauthorized.body}</p>
            <Button onClick={reloadPage} size="sm" variant="secondary">
              {mg.unauthorized.reload}
            </Button>
          </Callout>
        ) : !data && graph.errorKind === "unavailable" ? (
          <Callout announce title={mg.unavailable.title} tone="info">
            <p>{mg.unavailable.body}</p>
            <Button onClick={graph.reload} size="sm" variant="secondary">
              {mg.error.retry}
            </Button>
          </Callout>
        ) : !data && graph.errorKind !== null ? (
          <Callout announce title={mg.error.title} tone="danger">
            <p>{mg.error.body}</p>
            <Button onClick={graph.reload} size="sm" variant="secondary">
              {mg.error.retry}
            </Button>
          </Callout>
        ) : data ? (
          <div className="mg-container">
            <div className="mg-toolbar">
              <div className="mg-band">
                {sizeStatus && (
                  // The line changes under the reader (refresh, a resolved view
                  // turning unknown), so it is a live region — explicit
                  // `aria-live` alongside `role="status"`, matching the pattern
                  // the other announced surfaces in apps/web use.
                  <span
                    aria-live="polite"
                    className="mg-band__scale"
                    role="status"
                  >
                    {sizeStatus}
                  </span>
                )}
                {/* A reload that failed over a map that is still drawn. The
                    count line beside it already describes what is on screen, so
                    this one says only that the requested reload did not arrive
                    — and offers the retry inline rather than behind a panel
                    that would have hidden the map. */}
                {graph.errorKind !== null && (
                  <span className="mg-band__failure" role="alert">
                    {mg.error.reloadFailed}
                    <button
                      className="mg-band__retry"
                      onClick={graph.reload}
                      type="button"
                    >
                      {mg.error.retry}
                    </button>
                  </span>
                )}
              </div>
              <Button
                iconStart={<Icon icon={RefreshCw} size={14} />}
                loading={graph.refreshing}
                loadingLabel={messages.commitments.refreshing}
                onClick={graph.reload}
                size="sm"
                variant="secondary"
              >
                {messages.commitments.refresh}
              </Button>
            </div>
            {isNarrow ? (
              <>
                {partialNotice && (
                  // The narrow layout has no strip to keep to one line, so the
                  // detail stands open here rather than behind a control: the
                  // reason the wide surface folds it away does not apply.
                  <div className="mg-partial-note">
                    <p>{partialNotice.summary}</p>
                    <ul>
                      {partialNotice.details.map((line) => (
                        <li key={line}>{line}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {data.status === "unknown" ? (
                  <MemoryGraphUnknown data={data} messages={mg} />
                ) : data.nodes.length === 0 ? (
                  <EmptyState
                    description={mg.empty.body}
                    icon={Network}
                    title={mg.empty.title}
                  />
                ) : (
                  <NarrowClusterList
                    cards={narrowCards}
                    expandedClusterId={expandedClusterId}
                    messages={mg}
                    onFocusEntity={focusNode}
                    onSearchChange={onSearchChange}
                    onToggleCluster={toggleCluster}
                    searchValue={searchValue}
                  />
                )}
                {inspectorPanel}
              </>
            ) : (
              <div className="mg-workspace">
                <div className="mg-workspace__graph">
                  {/* The inspector is handed to the stage rather than dropped
                      beside the view: as a sibling of the whole view its
                      `100%` height counted the bottom strip too, so the panel
                      overhung the canvas by the strip's height and sat on the
                      strip's own text. */}
                  <GraphView
                    data={data}
                    interaction={interaction}
                    layout={layout}
                    messages={mg}
                    onFocusNode={focusNode}
                    onHoverNode={setHoveredNodeId}
                    onSearchChange={onSearchChange}
                    onZoomChange={setZoom}
                    overlay={inspectorPanel}
                    searchMatchCount={searchMatchCount}
                    searchValue={searchValue}
                  />
                </div>
              </div>
            )}
          </div>
        ) : null}
      </div>
    </AppShell>
  );
}

/**
 * Honest narrow unknown surface. Never presented as an empty graph: it states
 * the applicable body, the coverage progress and the coverage reasons, mirroring
 * the wide {@link GraphView} unknown surface.
 */
function MemoryGraphUnknown({
  data,
  messages,
}: {
  data: UnknownMemoryGraph;
  messages: MemoryGraphMessages;
}) {
  const body =
    data.reason === "no_authorized_evidence"
      ? messages.unknown.evidenceBody
      : messages.unknown.coverageBody[data.coverage.durability ?? "unstated"];
  const reasons = data.coverage.reasons;
  return (
    <Callout announce title={messages.unknown.title} tone="info">
      <p>{body}</p>
      <p className="mg-coverage-note">
        {messages.unknown.coverageProgress(
          String(data.coverage.reconciledSourceCount),
          String(data.coverage.requiredSourceCount),
        )}
      </p>
      {reasons.length > 0 && (
        <>
          <strong>{messages.unknown.reasonsTitle}</strong>
          <ul className="mg-coverage-reasons">
            {reasons.map((reason) => (
              <li key={reason}>{reasonSentence(reason, messages)}</li>
            ))}
          </ul>
        </>
      )}
    </Callout>
  );
}

function MemoryGraphLoading({ label }: { label: string }) {
  return (
    <div
      aria-busy="true"
      aria-label={label}
      className="mg-loading"
      role="status"
    >
      <span className="obsina-sr-only">{label}</span>
      <Skeleton height="2rem" width="40%" />
      <Skeleton height="22rem" />
    </div>
  );
}
